import { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { User } from "@models/user";
import { getUser, login, logout, register } from "../api/auth.service";
import Loading from "@app/pages/loading/Loading";
import { RegisterModel } from "@models/register";
import { useDispatch } from "react-redux";
import { logError, normalizeError } from "@shared/lib/errors";

interface AuthContextType {
  user: User | null;
  loginUser: (email: string, password: string) => Promise<User>;
  logoutUser: () => Promise<void>;
  registerUser: (registerInfo: RegisterModel) => Promise<User>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loginUser: async () => Promise.reject(new Error("loginUser not initialized")),
  logoutUser: async () => {},
  registerUser: async () => Promise.reject(new Error("registerUser not initialized")),
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const currentUser = await getUser();
      setUser(currentUser);
    } catch (error) {
      const appError = normalizeError(error);
      if (appError.kind !== "auth") {
        logError(appError, "auth-check-user-status");
      }
      setUser(null);
    }
    setLoading(false);
  };

  const loginUser = async (email: string, password: string): Promise<User> => {
    const currentUser = await login(email, password);
    setUser(currentUser);
    return currentUser;
  };

  const registerUser = async (registerInfo: RegisterModel): Promise<User> => {
    const currentUser = await register(registerInfo);
    setUser(currentUser);
    setLoading(false);
    return currentUser;
  };

  const logoutUser = async () => {
    try {
      await logout();
    } catch (error) {
      const appError = normalizeError(error);
      if (appError.kind !== "auth") {
        logError(appError, "auth-logout");
      }
      // Ignore logout failures and clear local auth state anyway.
    } finally {
      dispatch({ type: "logout" });
      setUser(null);
    }
  };

  const contextData: AuthContextType = {
    user,
    loginUser,
    logoutUser,
    registerUser,
  };

  return <AuthContext.Provider value={contextData}>{loading ? <Loading /> : children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
